import { Component, Input, OnChanges, ViewChild } from '@angular/core';
import { BarSeriesService, CategoryService, ColumnSeriesService, LineSeriesService, StripLineService, TooltipService, LegendService, DataLabelService } from '@syncfusion/ej2-angular-charts';
import { GridComponent, ToolbarService, ExcelExportService, PdfExportService } from '@syncfusion/ej2-angular-grids';
import { AchievementAction, AchievementStaff, AchievementStatus, StaffAchievementReport } from '../../../../shared/models/staff-achievement.model';
import { achievementColors, achievementLabels, achievementPercent, countResultLabel } from './achievement-presentation';
import { staffTargetDays, staffTargetDayLabel } from '../../../../shared/models/staff-reporting-calendar.model';

import { staffWorkDuration, staffWorkTimeHint } from '../../../../shared/functions/staff-work-duration';

let achievementInstance = 0;
@Component({
  selector: 'app-staff-achievement',
  templateUrl: './staff-achievement.component.html',
  styleUrls: ['./staff-achievement.component.scss'],
  providers: [BarSeriesService, CategoryService, ColumnSeriesService, LineSeriesService, StripLineService, TooltipService, LegendService, DataLabelService,
    ToolbarService, ExcelExportService, PdfExportService]
})
export class StaffAchievementComponent implements OnChanges {
  readonly duration = staffWorkDuration;
  timeHint(value: number | null): string { return staffWorkTimeHint(value, this.report?.context.daily_capacity_minutes); }
  readonly targetDays = staffTargetDays;
  readonly targetDayLabel = staffTargetDayLabel;
  @Input() report: StaffAchievementReport | null = null;
  @Input() loading = false;
  @Input() error = '';
  @ViewChild('timedGrid') timedGrid: GridComponent;
  @ViewChild('untimedGrid') untimedGrid: GridComponent;
  readonly id = 'staff-achievement-' + (++achievementInstance);
  readonly labels = achievementLabels;
  readonly colors = achievementColors;
  readonly percent = achievementPercent;
  selected: AchievementStaff | null = null;
  staffChart: any[] = [];
  timedRows: any[] = [];
  untimedRows: any[] = [];
  timedChart: any[] = [];
  untimedChart: any[] = [];
  timedCount = 0;
  untimedCount = 0;
  timedPage = 1;
  untimedPage = 1;
  actionSearch = '';
  statusFilter: AchievementStatus | 'all' = 'all';
  open = true;
  showBenchmarks = false;
  dailyWork: Array<{ date: string; weighted_minutes: number | null; capacity_minutes: number }> = [];
  dailyWorkAvailable = false;
  scope = '';
  xAxis: any = { valueType: 'Category', labelIntersectAction: 'None', maximumLabelWidth: 220, isInversed: true };
  percentAxis: any = { title: 'Benchmark achievement (%)', minimum: 0, stripLines: [{ start: 100, end: 100, size: 2, sizeType: 'Pixel', color: '#175cd3', dashArray: '5,3', text: '100% target', textStyle: { color: '#175cd3' } }] };
  staffPercentAxis: any;
  timedPercentAxis: any;
  untimedPercentAxis: any;
  dateAxis: any = { valueType: 'Category', title: 'Reporting date (Brisbane)', labelIntersectAction: 'Rotate45' };
  minutesAxis: any = { title: 'Weighted Work Minutes', minimum: 0, maximum: null, stripLines: [] };
  benchmarkMinutesAxis: any = { title: 'Benchmark-eligible weighted minutes', minimum: 0 };
  countAxis: any = { title: 'Actions completed', minimum: 0, maximum: null, stripLines: [] };
  dataLabel: any = { dataLabel: { visible: true, name: 'value_label', position: 'Outer' } };
  tooltip: any = { enable: true };
  legend = { visible: true };
  toolbar = ['ExcelExport', 'PdfExport'];
  private previousReport: StaffAchievementReport | null = null;

  ngOnChanges(): void {
    if (!this.report) { this.selected = null; this.staffChart = []; this.timedRows = []; this.untimedRows = []; return; }
    const previousUser = this.selected?.user_id;
    this.staffChart = this.report.staff.map(row => ({
      ...row, chart_label: row.name + ' (#' + row.user_id + ')',
      chart_value: this.showBenchmarks ? row.benchmark_percent : row.weighted_minutes,
      color: this.showBenchmarks ? this.colors[row.benchmark_status] : '#175cd3',
      value_label: this.showBenchmarks ? this.percent(row.benchmark_percent, row.benchmark_status) : this.duration(row.weighted_minutes)
    }));
    this.scope = this.scopeLabel();
    this.staffPercentAxis = this.axisFor(this.staffChart.map(row => row.benchmark_percent));
    this.selected = this.report.staff.length === 1 ? this.report.staff[0] : this.report.staff.find(row => row.user_id === previousUser) || null;
    if (this.previousReport !== this.report) { this.actionSearch = ''; this.statusFilter = 'all'; this.timedPage = 1; this.untimedPage = 1; }
    this.previousReport = this.report;
    this.filterActions();
  }
  toggleBenchmarks(): void {
    this.showBenchmarks = !this.showBenchmarks; this.statusFilter = 'all'; this.ngOnChanges();
    // Use the grid API so headers, cells and exported columns change together.
    for (const [grid, fields] of [
      [this.timedGrid, ['target_weighted_minutes', 'variance_minutes', 'percent_label']],
      [this.untimedGrid, ['percent_label']]
    ] as Array<[GridComponent, string[]]>) {
      if (grid) { this.showBenchmarks ? grid.showColumns(fields, 'field') : grid.hideColumns(fields, 'field'); }
    }
  }
  selectStaff(userId: number): void {
    this.selected = this.report?.staff.find(row => row.user_id === userId) || null;
    this.actionSearch = ''; this.statusFilter = 'all'; this.timedPage = 1; this.untimedPage = 1; this.filterActions();
  }
  staffClick(event: any): void { const row = this.staffChart[event.pointIndex]; if (row) { this.selectStaff(row.user_id); } }
  private scopeLabel(): string {
    const filters = this.report.context.filters;
    const parts: string[] = [];
    const names = { brand_id: 'Company', brand_report_id: 'Reporting company', brand_type_id: 'Brand type', subject_id: 'Subject', subject_number: 'Subject number', job_id: 'Job', created_level: 'Activity user level', access_level: 'Staff access level', ip_address: 'IP address' };
    for (const key of Object.keys(names)) { if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '' && filters[key] != 0) { parts.push(names[key] + ': ' + filters[key]); } }
    if (filters.action_ids !== null && filters.action_ids !== undefined) {
      parts.push('Actions: ' + (filters.action_ids.map(id => this.report.available_actions.find(a => a.log_type_id === id)?.name || '#' + id).join(', ') || 'none selected'));
    }
    return parts.length ? 'Limited scope — ' + parts.join(' · ') + '. Results describe only this selection.' : 'Scope: all actions for the selected active staff and reporting period.';
  }
  filtered(actions: AchievementAction[]): AchievementAction[] {
    const term = this.actionSearch.trim().toLocaleLowerCase();
    return actions.filter(row => (!term || (row.name + ' ' + row.log_type_id).toLocaleLowerCase().includes(term)) && (this.statusFilter === 'all' || row.status === this.statusFilter));
  }
  resetActionPages(): void { this.timedPage = 1; this.untimedPage = 1; this.filterActions(); }
  filterActions(): void {
    const timed = this.filtered(this.selected?.timed_actions || []);
    const untimed = this.filtered(this.selected?.untimed_actions || []);
    this.timedCount = timed.length; this.untimedCount = untimed.length;
    this.timedPage = Math.min(this.timedPage, this.pages(timed.length)); this.untimedPage = Math.min(this.untimedPage, this.pages(untimed.length));
    this.timedRows = timed.slice((this.timedPage - 1) * 10, this.timedPage * 10).map(row => this.presentAction(row));
    this.untimedRows = untimed.slice((this.untimedPage - 1) * 10, this.untimedPage * 10).map(row => this.presentAction(row));
    this.timedChart = this.timedRows.filter(row => this.showBenchmarks ? row.achievement_percent !== null : row.weighted_minutes !== null);
    this.untimedChart = this.untimedRows.filter(row => !this.showBenchmarks || row.achievement_percent !== null);
    const targetDates = this.report?.context.target_dates || this.report?.context.calendar_dates || [];
    this.dailyWork = (this.selected?.daily || []).map(day => ({
      date: day.date,
      // Earlier snapshots contain only the benchmark-eligible daily series. Reuse
      // it only when no untargeted weighted work is missing from that series.
      weighted_minutes: day.weighted_minutes !== undefined ? day.weighted_minutes :
        (this.selected.additional_weighted_minutes === 0 ? day.eligible_weighted_minutes : null),
      capacity_minutes: targetDates.includes(day.date) ? this.report.context.daily_capacity_minutes : 0
    }));
    this.dailyWorkAvailable = this.dailyWork.length > 0 && this.dailyWork.every(day => day.weighted_minutes !== null);
    this.timedPercentAxis = this.axisFor(this.timedChart.map(row => row.achievement_percent));
    this.untimedPercentAxis = this.axisFor(this.untimedChart.map(row => row.achievement_percent));
  }
  private axisFor(values: Array<number | null>): any { return { ...this.percentAxis, maximum: Math.max(115, ...values.map(value => (value || 0) * 1.18)) }; }
  minutes(value: number | null): string { return value === null || value === undefined ? 'Unavailable' : value.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
  variance(value: number | null): string {
    if (value === null || value === undefined) { return 'Variance unavailable'; }
    if (value === 0) { return 'Exactly on target'; }
    const amount = Math.abs(value) < 0.005 ? '<0.01' : this.minutes(Math.abs(value));
    return amount + ' minute ' + (value < 0 ? 'shortfall' : 'surplus');
  }
  staffTooltip(args: any): void {
    const row = this.staffChart[args.point.index];
    if (row && !this.showBenchmarks) {
      args.text = row.name + ': ' + row.count + ' actions; ' + this.minutes(row.weighted_minutes) + ' weighted minutes (' + this.timeHint(row.weighted_minutes) + '); daily work-target coverage ' + this.percent(row.daily_coverage_percent, row.daily_coverage_status); return;
    }
    if (row) { args.text = row.name + ': ' + this.percent(row.benchmark_percent, row.benchmark_status) + ' — ' + this.labels[row.benchmark_status] + '; ' + this.minutes(row.eligible_weighted_minutes) + ' / ' + this.minutes(row.target_weighted_minutes) + ' weighted minutes (' + this.duration(row.eligible_weighted_minutes) + ' / ' + this.duration(row.target_weighted_minutes) + ')'; }
  }
  pages(count: number): number { return Math.max(1, Math.ceil(count / 10)); }
  page(kind: 'timed' | 'untimed', delta: number): void { if (kind === 'timed') { this.timedPage += delta; } else { this.untimedPage += delta; } this.filterActions(); }
  chartHeight(count: number): string { return Math.max(180, count * 44 + 70) + 'px'; }
  private presentAction(row: AchievementAction): any {
    return { ...row, status_label: row.status === 'no_target' ? (row.unit_per_day > 0 ? 'No target in this period' : 'No target configured') : this.labels[row.status], percent_label: this.percent(row.achievement_percent, row.status),
      count_result_label: countResultLabel(row.status, row.unit_per_day),
      count_difference: row.status === 'no_target' || row.status === 'unavailable' ? null : row.count_variance,
      chart_label: this.showBenchmarks ? row.name + ' (' + row.count + ' / ' + (row.expected_count ?? 'N/A') + ')' : row.name + ' (' + row.count + ' actions)',
      color: this.showBenchmarks ? this.colors[row.status] : '#175cd3',
      value_label: this.showBenchmarks ? this.percent(row.achievement_percent, row.status) : (row.kind === 'timed' ? this.duration(row.weighted_minutes) : String(row.count)) };
  }
  actionTooltip(args: any, kind: 'timed' | 'untimed'): void {
    const row = (kind === 'timed' ? this.timedChart : this.untimedChart)[args.point.index];
    if (!row) { return; }
    if (!this.showBenchmarks) {
      args.text = row.name + ': ' + row.count + ' actions';
      if (kind === 'timed') { args.text += '; ' + row.minutes_per_action + ' min/action; ' + this.minutes(row.weighted_minutes) + ' weighted minutes (' + this.timeHint(row.weighted_minutes) + ')'; }
      else { args.text += '; no weighted minutes'; }
      return;
    }
    args.text = row.name + ': ' + row.count + ' / ' + row.expected_count + ' occurrences; ' + row.percent_label + ' — ' + row.status_label;
    if (kind === 'timed') { args.text += '; ' + row.minutes_per_action + ' min/action; ' + row.weighted_minutes.toFixed(2) + ' / ' + row.target_weighted_minutes.toFixed(2) + ' min; variance ' + row.variance_minutes.toFixed(2) + ' min; time equivalent ' + this.duration(row.weighted_minutes) + ' / ' + this.duration(row.target_weighted_minutes); }
  }
  dailyTooltip(args: any): void {
    const rows = this.showBenchmarks ? this.selected?.daily : this.dailyWork;
    const row = rows?.[args.point.index];
    const value = row?.[args.series.yName];
    if (value !== null && value !== undefined) { args.text = args.series.name + ': ' + this.minutes(value) + ' min (' + this.timeHint(value) + ')'; }
  }
  exportActions(args: any, kind: 'timed' | 'untimed'): void {
    const grid = kind === 'timed' ? this.timedGrid : this.untimedGrid;
    // Export the same filtered ten rows visible in the chart and grid.
    const rows = kind === 'timed' ? this.timedRows : this.untimedRows;
    const name = 'Staff-' + this.selected.user_id + '-' + kind + '-achievements';
    if (args.item.id.endsWith('_excelexport')) { grid.excelExport({ fileName: name + '.xlsx', dataSource: rows }); }
    if (args.item.id.endsWith('_pdfexport')) { grid.pdfExport({ fileName: name + '.pdf', dataSource: rows, pageOrientation: 'Landscape' }); }
  }
}
